import { useState } from 'react';

export default function AccessoryFilterPanel({ filters, setFilters }) {
    const [expandedSections, setExpandedSections] = useState({
        type: true,
        brand: true,
        price: true,
        compatibility: true,
        connectivity: true,
        power: true,
        availability: true,
        discount: true,
        rating: true
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const accessoryTypes = ['Chargers', 'Earphones', 'Power Banks', 'Cables', 'Covers', 'Mounts', 'Remotes'];
    const brands = ['Samsung', 'Apple', 'Anker', 'Belkin', 'Spigen', 'Xiaomi', 'JBL', 'Sony', 'Bose', 'Logitech', 'OtterBox', 'UAG'];
    const compatibilityOptions = ['Mobile', 'Tablet', 'TV'];
    const connectivityOptions = ['Wired', 'Wireless', 'N/A'];
    const powerOutputOptions = ['N/A', '18W', '20W', '25W', '30W', '33W', '65W', '100W'];

    const handleTypeChange = (type) => {
        const newTypes = filters.types.includes(type)
            ? filters.types.filter(t => t !== type)
            : [...filters.types, type];
        setFilters({ ...filters, types: newTypes });
    };

    const handleBrandChange = (brand) => {
        const newBrands = filters.brands.includes(brand)
            ? filters.brands.filter(b => b !== brand)
            : [...filters.brands, brand];
        setFilters({ ...filters, brands: newBrands });
    };

    const handleCompatibilityChange = (comp) => {
        const newComp = filters.compatibility.includes(comp)
            ? filters.compatibility.filter(c => c !== comp)
            : [...filters.compatibility, comp];
        setFilters({ ...filters, compatibility: newComp });
    };

    const handleConnectivityChange = (conn) => {
        const newConn = filters.connectivity.includes(conn)
            ? filters.connectivity.filter(c => c !== conn)
            : [...filters.connectivity, conn];
        setFilters({ ...filters, connectivity: newConn });
    };

    const handlePowerOutputChange = (power) => {
        const newPower = filters.powerOutput.includes(power)
            ? filters.powerOutput.filter(p => p !== power)
            : [...filters.powerOutput, power];
        setFilters({ ...filters, powerOutput: newPower });
    };

    const clearAllFilters = () => {
        setFilters({
            types: [],
            brands: [],
            priceRange: [0, 50000],
            compatibility: [],
            connectivity: [],
            powerOutput: [],
            availability: false,
            discount: false,
            rating: 0
        });
    };

    return (
        <div className="bg-card rounded-lg border border-border">
            {/* Header */}
            <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="font-bold text-foreground">Filters</h3>
                <button
                    onClick={clearAllFilters}
                    className="text-sm text-primary hover:text-primary/85 font-medium transition-colors whitespace-nowrap"
                >
                    Clear All
                </button>
            </div>

            <div className="divide-y divide-gray-200">
                {/* Accessory Type */}
                <div className="p-4">
                    <button
                        onClick={() => toggleSection('type')}
                        className="w-full flex items-center justify-between mb-3 cursor-pointer"
                    >
                        <span className="font-semibold text-foreground text-sm">Accessory Type</span>
                        <i className={`ri-arrow-${expandedSections.type ? 'up' : 'down'}-s-line text-muted-foreground`}></i>
                    </button>
                    {expandedSections.type && (
                        <div className="space-y-2">
                            {accessoryTypes.map(type => (
                                <label key={type} className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={filters.types.includes(type)}
                                        onChange={() => handleTypeChange(type)}
                                        className="w-4 h-4 rounded border-border text-[#FF6B35] focus:ring-[#FF6B35] cursor-pointer"
                                    />
                                    <span className="text-sm text-foreground/80 group-hover:text-foreground">{type}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                {/* Brand */}
                <div className="p-4">
                    <button
                        onClick={() => toggleSection('brand')}
                        className="w-full flex items-center justify-between mb-3 cursor-pointer"
                    >
                        <span className="font-semibold text-foreground text-sm">Brand</span>
                        <i className={`ri-arrow-${expandedSections.brand ? 'up' : 'down'}-s-line text-muted-foreground`}></i>
                    </button>
                    {expandedSections.brand && (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {brands.map(brand => (
                                <label key={brand} className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={filters.brands.includes(brand)}
                                        onChange={() => handleBrandChange(brand)}
                                        className="w-4 h-4 rounded border-border text-[#FF6B35] focus:ring-[#FF6B35] cursor-pointer"
                                    />
                                    <span className="text-sm text-foreground/80 group-hover:text-foreground">{brand}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                {/* Price Range */}
                <div className="p-4">
                    <button
                        onClick={() => toggleSection('price')}
                        className="w-full flex items-center justify-between mb-3 cursor-pointer"
                    >
                        <span className="font-semibold text-foreground text-sm">Price Range</span>
                        <i className={`ri-arrow-${expandedSections.price ? 'up' : 'down'}-s-line text-muted-foreground`}></i>
                    </button>
                    {expandedSections.price && (
                        <div className="space-y-3">
                            <input
                                type="range"
                                min="0"
                                max="50000"
                                step="500"
                                value={filters.priceRange[1]}
                                onChange={(e) => setFilters({ ...filters, priceRange: [0, parseInt(e.target.value)] })}
                                className="w-full accent-primary cursor-pointer"
                            />
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">KSh 0</span>
                                <span className="font-semibold text-foreground">KSh {filters.priceRange[1].toLocaleString()}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Compatibility */}
                <div className="p-4">
                    <button
                        onClick={() => toggleSection('compatibility')}
                        className="w-full flex items-center justify-between mb-3 cursor-pointer"
                    >
                        <span className="font-semibold text-foreground text-sm">Compatibility</span>
                        <i className={`ri-arrow-${expandedSections.compatibility ? 'up' : 'down'}-s-line text-muted-foreground`}></i>
                    </button>
                    {expandedSections.compatibility && (
                        <div className="space-y-2">
                            {compatibilityOptions.map(comp => (
                                <label key={comp} className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={filters.compatibility.includes(comp)}
                                        onChange={() => handleCompatibilityChange(comp)}
                                        className="w-4 h-4 rounded border-border text-[#FF6B35] focus:ring-[#FF6B35] cursor-pointer"
                                    />
                                    <span className="text-sm text-foreground/80 group-hover:text-foreground">{comp}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                {/* Connectivity Type */}
                <div className="p-4">
                    <button
                        onClick={() => toggleSection('connectivity')}
                        className="w-full flex items-center justify-between mb-3 cursor-pointer"
                    >
                        <span className="font-semibold text-foreground text-sm">Connectivity Type</span>
                        <i className={`ri-arrow-${expandedSections.connectivity ? 'up' : 'down'}-s-line text-muted-foreground`}></i>
                    </button>
                    {expandedSections.connectivity && (
                        <div className="space-y-2">
                            {connectivityOptions.map(conn => (
                                <label key={conn} className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={filters.connectivity.includes(conn)}
                                        onChange={() => handleConnectivityChange(conn)}
                                        className="w-4 h-4 rounded border-border text-[#FF6B35] focus:ring-[#FF6B35] cursor-pointer"
                                    />
                                    <span className="text-sm text-foreground/80 group-hover:text-foreground">{conn}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                {/* Power Output */}
                <div className="p-4">
                    <button
                        onClick={() => toggleSection('power')}
                        className="w-full flex items-center justify-between mb-3 cursor-pointer"
                    >
                        <span className="font-semibold text-foreground text-sm">Power Output</span>
                        <i className={`ri-arrow-${expandedSections.power ? 'up' : 'down'}-s-line text-muted-foreground`}></i>
                    </button>
                    {expandedSections.power && (
                        <div className="space-y-2">
                            {powerOutputOptions.map(power => (
                                <label key={power} className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={filters.powerOutput.includes(power)}
                                        onChange={() => handlePowerOutputChange(power)}
                                        className="w-4 h-4 rounded border-border text-[#FF6B35] focus:ring-[#FF6B35] cursor-pointer"
                                    />
                                    <span className="text-sm text-foreground/80 group-hover:text-foreground">{power}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                {/* Availability */}
                <div className="p-4">
                    <button
                        onClick={() => toggleSection('availability')}
                        className="w-full flex items-center justify-between mb-3 cursor-pointer"
                    >
                        <span className="font-semibold text-foreground text-sm">Availability</span>
                        <i className={`ri-arrow-${expandedSections.availability ? 'up' : 'down'}-s-line text-muted-foreground`}></i>
                    </button>
                    {expandedSections.availability && (
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="checkbox"
                                checked={filters.availability}
                                onChange={(e) => setFilters({ ...filters, availability: e.target.checked })}
                                className="w-4 h-4 rounded border-border text-[#FF6B35] focus:ring-[#FF6B35] cursor-pointer"
                            />
                            <span className="text-sm text-foreground/80 group-hover:text-foreground">In Stock Only</span>
                        </label>
                    )}
                </div>

                {/* Discount */}
                <div className="p-4">
                    <button
                        onClick={() => toggleSection('discount')}
                        className="w-full flex items-center justify-between mb-3 cursor-pointer"
                    >
                        <span className="font-semibold text-foreground text-sm">Discount</span>
                        <i className={`ri-arrow-${expandedSections.discount ? 'up' : 'down'}-s-line text-muted-foreground`}></i>
                    </button>
                    {expandedSections.discount && (
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="checkbox"
                                checked={filters.discount}
                                onChange={(e) => setFilters({ ...filters, discount: e.target.checked })}
                                className="w-4 h-4 rounded border-border text-[#FF6B35] focus:ring-[#FF6B35] cursor-pointer"
                            />
                            <span className="text-sm text-foreground/80 group-hover:text-foreground">Discounted Items</span>
                        </label>
                    )}
                </div>

                {/* Rating */}
                <div className="p-4">
                    <button
                        onClick={() => toggleSection('rating')}
                        className="w-full flex items-center justify-between mb-3 cursor-pointer"
                    >
                        <span className="font-semibold text-foreground text-sm">Ratings</span>
                        <i className={`ri-arrow-${expandedSections.rating ? 'up' : 'down'}-s-line text-muted-foreground`}></i>
                    </button>
                    {expandedSections.rating && (
                        <div className="space-y-2">
                            {[4, 3, 2, 1].map(rating => (
                                <label key={rating} className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                        type="radio"
                                        name="rating"
                                        checked={filters.rating === rating}
                                        onChange={() => setFilters({ ...filters, rating })}
                                        className="w-4 h-4 border-border text-[#FF6B35] focus:ring-[#FF6B35] cursor-pointer"
                                    />
                                    <div className="flex items-center gap-1">
                                        {[...Array(5)].map((_, i) => (
                                            <i
                                                key={i}
                                                className={`ri-star-${i < rating ? 'fill' : 'line'} text-sm ${i < rating ? 'text-yellow-400' : 'text-muted-foreground/40'
                                                    }`}
                                            ></i>
                                        ))}
                                        <span className="text-sm text-foreground/80 ml-1">&amp; Up</span>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
